import { useMutation, useQuery, type UseQueryReturnType } from '@tanstack/vue-query';
import axiosInstance from '@/plugins/axios';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import type {
  IUpgradeResponse,
  IVerifiedWhitelistResponse,
  IWhitelistEmailRequest,
  IWhitelistResponse
} from '@/types/services/settings.service';
import type { DefaultError } from '@tanstack/query-core';
import useSnackbar from '../useSnackbar';
import { useI18n } from 'vue-i18n';

const useSettingsService = () => {
  const { t } = useI18n();
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();
  const { addSnackbar } = useSnackbar();

  const getVerifiedWhitelist = (): UseQueryReturnType<IVerifiedWhitelistResponse, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'settings', 'whitelist', 'verified'],
      queryFn: async ({ signal }) => {
        const res = await axiosInstance.get(`/u/${userIndex.value}/settings/whitelist/verified`, {
          signal
        });
        return res.data;
      },
      enabled: queriesEnabled
    });
  };

  const getWhitelist = (): UseQueryReturnType<IWhitelistResponse, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'settings', 'whitelist', 'list'],
      queryFn: async ({ signal }) => {
        const res = await axiosInstance.get(`/u/${userIndex.value}/settings/whitelist`, {
          signal
        });
        return res.data;
      },
      enabled: queriesEnabled
    });
  };

  const deleteEmail = () => {
    return useMutation<void, DefaultError, string>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'whitelist', 'delete'],
      mutationFn: async (id: string) => {
        return axiosInstance.delete(`/u/${userIndex.value}/settings/whitelist/${id}`);
      },
      onSuccess() {
        addSnackbar({
          message: t('settings.outgoingEmails.actions.delete.success'),
          type: 'success'
        });
      }
    });
  };

  const updateEmail = () =>
    useMutation<void, DefaultError, { id: string; data: IWhitelistEmailRequest }>({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'whitelist', 'update'],
      mutationFn: async ({ id, data }) => {
        await axiosInstance.patch(`/u/${userIndex.value}/settings/whitelist/${id}`, data);
      },
      onSuccess: () => {
        addSnackbar({
          message: t('settings.outgoingEmails.actions.edit.success'),
          type: 'success'
        });
      }
    });

  // Upgrade
  const getUpgrade = (): UseQueryReturnType<IUpgradeResponse[], DefaultError> =>
    useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'settings', 'upgrade'],
      queryFn: async () => {
        return (await axiosInstance.get(`/u/${userIndex.value}/settings/upgrade`)).data;
      },
      enabled: queriesEnabled
    });

  // Upgrade
  const resetUpgrade = () =>
    useMutation({
      mutationKey: ['authenticatedQuery', userIndex, 'settings', 'upgrade', 'reset'],
      mutationFn: async () => {
        await axiosInstance.post(`/u/${userIndex.value}/settings/upgrade/reset`);
      }
    });

  return { getVerifiedWhitelist, getWhitelist, deleteEmail, updateEmail, getUpgrade, resetUpgrade };
};

export default useSettingsService;
